import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  CssBaseline,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { createWidgetTheme } from '@shared/theme/widgetTheme';
import { WebSocketCallService } from '@shared/services/websocketCallService';
import { publicWidgetUrl, type PublicWidget, type WidgetGrant } from './publicWidgetApi';
import { httpsUrl, normalizedOrigin } from '@features/Workspace/widgetValidation';

type Line = { id: string; actor: string; text: string; status?: string };
const uuid = (v: unknown): v is string => typeof v === 'string' && /^[a-f0-9-]{36}$/i.test(v);
const safeSocket = (value: string) => {
  try {
    const u = new URL(value);
    return (
      !u.username &&
      !u.password &&
      !u.search &&
      !u.hash &&
      (u.protocol === 'wss:' ||
        (u.protocol === 'ws:' && ['localhost', '127.0.0.1', '[::1]'].includes(u.hostname)))
    );
  } catch {
    return false;
  }
};
/** An unsigned decode only rejects mismatched visitor intent. The worker authenticates the signature. */
export const trustedWidgetGrant = (
  grant: unknown,
  config: PublicWidget,
  parentOrigin: string,
): grant is WidgetGrant => {
  const g = grant as WidgetGrant;
  if (
    !g ||
    !config.available ||
    !config.appearance ||
    !config.versionId ||
    !uuid(g.sessionId) ||
    typeof g.token !== 'string' ||
    !g.token ||
    g.token.length > 4096 ||
    typeof g.expiresAt !== 'string' ||
    !Number.isFinite(Date.parse(g.expiresAt)) ||
    Date.parse(g.expiresAt) <= Date.now() ||
    g.websocketUrl !== config.websocketUrl ||
    !safeSocket(g.websocketUrl)
  )
    return false;
  try {
    const parts = g.token.split('.');
    if (parts.length !== 3 || parts.some((part) => !part || !/^[A-Za-z0-9_-]+$/.test(part)))
      return false;
    const binary = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(
      new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0))),
    ) as Record<string, unknown>;
    return (
      !!claims &&
      !Array.isArray(claims) &&
      claims.sid === g.sessionId &&
      claims.wid === config.id &&
      claims.vid === config.versionId &&
      claims.mode === config.appearance.mode &&
      claims.origin === parentOrigin &&
      typeof claims.iat === 'number' &&
      Number.isSafeInteger(claims.iat) &&
      claims.iat >= 0 &&
      claims.iat * 1000 <= Date.now() + 5000 &&
      typeof claims.exp === 'number' &&
      Number.isSafeInteger(claims.exp) &&
      claims.exp > claims.iat &&
      claims.exp - claims.iat <= 120 &&
      claims.exp * 1000 > Date.now() &&
      Math.floor(Date.parse(g.expiresAt) / 1000) === claims.exp
    );
  } catch {
    return false;
  }
};
export const PublicWidgetPage = () => {
  const { widgetId = '' } = useParams();
  return <PublicSession key={widgetId} id={widgetId} />;
};
const PublicSession = ({ id }: { id: string }) => {
  const params = new URLSearchParams(window.location.search),
    parentOrigin = params.get('parentOrigin'),
    nonce = params.get('nonce');
  const embedded =
    window.parent !== window &&
    !!parentOrigin &&
    normalizedOrigin(parentOrigin) === parentOrigin &&
    !!nonce &&
    uuid(nonce);
  const [config, setConfig] = useState<PublicWidget | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('');
  const [phase, setPhase] = useState<
    'idle' | 'grant' | 'joining' | 'ready' | 'ended' | 'uncertain'
  >('idle');
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [terms, setTerms] = useState(false),
    [text, setText] = useState(''),
    [lines, setLines] = useState<Line[]>([]),
    [collapsed, setCollapsed] = useState(false);
  const configRequest = useRef<AbortController | null>(null);
  const service = useRef<WebSocketCallService | null>(null),
    audio = useRef<AudioContext | null>(null),
    active = useRef(true),
    epoch = useRef(0),
    request = useRef<string | null>(null);
  const latest = useRef({ config, phase, terms });
  latest.current = { config, phase, terms };
  const agentOpen = useRef<Record<string, string>>({}),
    sequence = useRef(0);
  const stop = useCallback(() => {
    epoch.current++;
    configRequest.current?.abort();
    configRequest.current = null;
    const owned = service.current;
    service.current = null;
    const context = audio.current;
    audio.current = null;
    owned?.destroy();
    if (!owned && context && context.state !== 'closed') void context.close().catch(() => {});
    request.current = null;
    agentOpen.current = {};
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setTerms(false);
    const generation = ++epoch.current;
    configRequest.current?.abort();
    const controller = new AbortController();
    configRequest.current = controller;
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const res = await fetch(publicWidgetUrl(id), {
        credentials: 'omit',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res.ok) throw new Error();
      const result: PublicWidget = await res.json();
      if (active.current && epoch.current === generation) {
        setRefreshRequired(false);
        setConfig(result);
        setCollapsed(result.appearance?.size === 'tiny');
        setPhase('idle');
      }
    } catch {
      if (active.current && epoch.current === generation) {
        setConfig(null);
        setError('This assistant is unavailable.');
      }
    } finally {
      clearTimeout(timeout);
      if (configRequest.current === controller) configRequest.current = null;
      if (active.current && epoch.current === generation) setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    active.current = true;
    void load();
    return () => {
      active.current = false;
      stop();
    };
  }, [load, stop]);
  useEffect(() => {
    if (!embedded || !config?.appearance) return;
    window.parent.postMessage(
      {
        channel: 'callevate-widget',
        type: 'layout',
        nonce,
        size: config.appearance.size,
        placement: config.appearance.placement,
        collapsed,
      },
      parentOrigin!,
    );
  }, [config, collapsed, embedded, nonce, parentOrigin]);
  const connect = useCallback(
    (grant: WidgetGrant, observedOrigin: string) => {
      const current = latest.current.config;
      if (!current || service.current || !trustedWidgetGrant(grant, current, observedOrigin))
        return;
      const generation = epoch.current;
      setPhase('joining');
      const ws = new WebSocketCallService({
        serviceUrl: '',
        campaignId: '',
        visitor: {
          url: grant.websocketUrl,
          token: grant.token,
          sessionId: grant.sessionId,
          mode: current.appearance!.mode,
        },
        preparedAudioContext: audio.current ?? undefined,
        onSessionReady: () => {
          if (active.current && epoch.current === generation) setPhase('ready');
        },
        onError: () => {
          if (active.current && epoch.current === generation) {
            setError(
              'The connection could not continue. End this conversation before starting another.',
            );
            setPhase('uncertain');
          }
        },
        onDisconnect: () => {
          if (active.current && epoch.current === generation) {
            stop();
            setPhase('ended');
          }
        },
        onTextAccepted: (accepted) => {
          if (active.current && epoch.current === generation)
            setLines((rows) =>
              rows.filter((row) => row.id !== accepted || row.status === 'Server transcript'),
            );
        },
        onTranscriptReceived: (chunk) => {
          if (!active.current || epoch.current !== generation || (!chunk.text && !chunk.final))
            return;
          const open = agentOpen.current[chunk.actor];
          const key = chunk.segmentId ?? open ?? `segment-${sequence.current++}`;
          if (chunk.final) delete agentOpen.current[chunk.actor];
          else agentOpen.current[chunk.actor] = key;
          if (!chunk.text) return;
          setLines((rows) => {
            const found = rows.find((row) => row.id === key);
            return found
              ? rows.map((row) =>
                  row.id === key
                    ? {
                        ...row,
                        text: !open && chunk.segmentId ? chunk.text : `${row.text} ${chunk.text}`,
                        status: chunk.actor === 'customer' ? 'Server transcript' : row.status,
                      }
                    : row,
                )
              : [
                  ...rows,
                  {
                    id: key,
                    actor: chunk.actor,
                    text: chunk.text,
                    status: chunk.actor === 'customer' ? 'Server transcript' : undefined,
                  },
                ];
          });
        },
      });
      service.current = ws;
      void ws.startCall().catch(() => {
        if (active.current && epoch.current === generation) {
          stop();
          setPhase('ended');
          setError(
            current.appearance!.mode === 'voice'
              ? 'Microphone or connection unavailable. Check browser permissions before starting again.'
              : 'Connection unavailable. Refresh configuration before starting again.',
          );
        }
      });
    },
    [stop],
  );
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (
        !embedded ||
        event.source !== window.parent ||
        event.origin !== parentOrigin ||
        event.data?.channel !== 'callevate-widget' ||
        event.data.nonce !== nonce ||
        event.data.requestId !== request.current ||
        latest.current.phase !== 'grant'
      )
        return;
      if (event.data.type === 'grant') {
        const current = latest.current.config;
        if (current && trustedWidgetGrant(event.data.grant, current, event.origin))
          connect(event.data.grant, event.origin);
        else {
          setPhase('uncertain');
          setError('The session response did not match the current published configuration.');
        }
      }
      if (event.data.type === 'denied') {
        if (event.data.status === 409) setRefreshRequired(true);
        setPhase(event.data.status === 409 ? 'ended' : 'uncertain');
        setError(
          event.data.status === 409
            ? 'The session request conflicts with the current configuration or capacity. Refresh configuration and review terms before trying again.'
            : event.data.reason === 'admission_pending'
              ? 'An earlier session request is still pending. Wait for it to finish before starting again.'
              : 'Session admission was not confirmed. No automatic retry will start a call.',
        );
      }
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [connect, embedded, nonce, parentOrigin]);
  const start = () => {
    const c = latest.current.config;
    if (
      !active.current ||
      refreshRequired ||
      request.current ||
      !embedded ||
      !c?.available ||
      !c.versionId ||
      !c.appearance ||
      !c.websocketUrl ||
      !safeSocket(c.websocketUrl) ||
      !['idle', 'ended'].includes(latest.current.phase) ||
      (c.appearance.termsEnabled && !latest.current.terms)
    )
      return;
    setError('');
    setLines([]);
    epoch.current++;
    request.current = crypto.randomUUID();
    setPhase('grant');
    if (c.appearance.mode === 'voice') {
      try {
        audio.current = new AudioContext({ sampleRate: 48000 });
        void audio.current.resume().catch(() => {});
      } catch {
        setError('Voice is not supported by this browser.');
        setPhase('ended');
        return;
      }
    }
    window.parent.postMessage(
      {
        channel: 'callevate-widget',
        type: 'start',
        nonce,
        requestId: request.current,
        versionId: c.versionId,
        mode: c.appearance.mode,
        termsAccepted: terms,
      },
      parentOrigin!,
    );
  };
  const theme = useMemo(
    () => createWidgetTheme(config?.appearance?.colors ?? {}),
    [config?.appearance?.colors],
  );
  const a = config?.appearance;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Paper
        component="main"
        aria-label="Public assistant"
        sx={{
          height: '100dvh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 2,
        }}>
        <Stack gap={2} sx={{ p: collapsed ? 1 : 2 }}>
          {loading ? (
            <CircularProgress aria-label="Loading assistant" />
          ) : !config?.available || !a ? (
            <Alert severity="info">This assistant is unavailable.</Alert>
          ) : (
            <>
              <Stack
                direction="row"
                gap={1.5}
                alignItems="center"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  p: collapsed ? 1 : 2,
                  borderRadius: 1,
                }}>
                <Avatar
                  sx={{ width: collapsed ? 32 : 40, height: collapsed ? 32 : 40 }}
                  src={a.avatar ?? undefined}
                  imgProps={{ referrerPolicy: 'no-referrer' }}>
                  {a.title.slice(0, 1)}
                </Avatar>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {collapsed ? a.cta : a.title}
                </Typography>
                <Button
                  sx={{ color: 'inherit' }}
                  aria-label={collapsed ? 'Open assistant' : 'Minimize assistant'}
                  onClick={() => setCollapsed(!collapsed)}>
                  {collapsed ? 'Open' : '−'}
                </Button>
              </Stack>
              {!collapsed && (
                <>
                  <Typography>{a.cta}</Typography>
                  {!embedded && (
                    <Alert severity="info">
                      Open this assistant using the website embed. Direct links cannot start
                      anonymous sessions.
                    </Alert>
                  )}
                  {a.termsEnabled && a.termsUrl && httpsUrl(a.termsUrl) && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={terms}
                          disabled={!['idle', 'ended'].includes(phase)}
                          onChange={(e) => setTerms(e.target.checked)}
                        />
                      }
                      label={
                        <span>
                          I accept the{' '}
                          <Link href={a.termsUrl} target="_blank" rel="noopener noreferrer">
                            terms
                          </Link>
                        </span>
                      }
                    />
                  )}
                  {['grant', 'joining'].includes(phase) && (
                    <Typography role="status">
                      {phase === 'grant' ? 'Requesting session…' : 'Joining conversation…'}
                    </Typography>
                  )}
                  {phase === 'ready' && <Typography role="status">Connected</Typography>}
                  {phase === 'ended' && <Typography role="status">Conversation ended</Typography>}
                  <Stack
                    component="section"
                    aria-label="Conversation"
                    aria-live="polite"
                    gap={1}
                    sx={{
                      minHeight: a.size === 'full' ? 200 : 80,
                      maxHeight: 340,
                      overflow: 'auto',
                    }}>
                    {lines.map((line) => (
                      <Box
                        key={line.id}
                        sx={{ p: 1.5, bgcolor: 'surface.sunken', borderRadius: 1 }}>
                        <Typography variant="caption">
                          {line.actor === 'agent' ? 'Assistant' : 'You'}
                          {line.status ? ` · ${line.status}` : ''}
                        </Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {line.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  {a.mode === 'chat' && (
                    <Box
                      component="form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const value = text.trim();
                        if (phase !== 'ready' || !value) return;
                        const id = crypto.randomUUID();
                        if (service.current?.sendText(id, value)) {
                          setLines((rows) => [
                            ...rows,
                            {
                              id,
                              actor: 'customer',
                              // Only the server projection may enter conversation history.
                              // Native SDK transcript IDs need not match this delivery receipt.
                              text: '',
                              status: 'Awaiting acknowledgement',
                            },
                          ]);
                          setText('');
                        }
                      }}>
                      <Stack direction="row" gap={1}>
                        <TextField
                          fullWidth
                          label={a.placeholder}
                          value={text}
                          disabled={phase !== 'ready'}
                          onChange={(e) => setText(e.target.value)}
                          slotProps={{ htmlInput: { maxLength: 4000 } }}
                        />
                        <Button type="submit" disabled={phase !== 'ready' || !text.trim()}>
                          Send
                        </Button>
                      </Stack>
                    </Box>
                  )}
                  <Stack direction="row" gap={1}>
                    {['idle', 'ended'].includes(phase) ? (
                      <Button
                        variant="contained"
                        disabled={refreshRequired || !embedded || (a.termsEnabled && !terms)}
                        onClick={start}>
                        {a.start}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          stop();
                          setPhase('ended');
                        }}>
                        {a.end}
                      </Button>
                    )}
                    <Button
                      disabled={!['idle', 'ended'].includes(phase)}
                      onClick={() => {
                        stop();
                        void load();
                      }}>
                      Refresh configuration
                    </Button>
                  </Stack>
                </>
              )}
            </>
          )}
          {!loading && !config?.available && (
            <Button
              onClick={() => {
                stop();
                void load();
              }}>
              Refresh configuration
            </Button>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </Paper>
    </ThemeProvider>
  );
};
